import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Paper, Avatar,
  Button, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton,
} from '@mui/material';
import {
  Phone as PhoneIcon, Email as EmailIcon,
  LocationOn as LocationOnIcon, LinkedIn as LinkedInIcon,
  Language as LanguageIcon, DriveEta as DriveEtaIcon,
  DragIndicator, Reorder, ChevronLeft, ChevronRight,
} from '@mui/icons-material';

const defaultLeftSectionOrder = ['skills', 'awards', 'references', 'certifications', 'languages', 'softwareSkills', 'interests'];
const defaultRightSectionOrder = ['summary', 'experience', 'education', 'accomplishments', 'additionalInfo', 'volunteering', 'websites'];

const pageSizeMap = {
  A4:        { width: "210mm", height: "297mm" },
  Letter:    { width: "216mm", height: "279mm" },
  Legal:     { width: "216mm", height: "356mm" },
  A3:        { width: "297mm", height: "420mm" },
  Executive: { width: "184mm", height: "267mm" },
};

const pageSizePxMap = {
  A4:        { width: 794,  height: 1123 },
  Letter:    { width: 816,  height: 1056 },
  Legal:     { width: 816,  height: 1344 },
  A3:        { width: 1123, height: 1587 },
  Executive: { width: 696,  height: 1010 },
};

export const resumeMeta = { hasPhoto: true, columns: 2 };

const Resume47 = ({
  theme = "#0d47a1",
  color = "#0d47a1",
  font = "Arial",
  fontSize = "14px",
  fontStyle = "normal",
  headingSize = "18px",
  sectionSpacing = 30,
  paragraphSpacing = 12,
  lineSpacing = 22,
  topBottomMargin = 30,
  sideMargins = 30,
  pageSize = "A4",
  formData = {},
  photo = null,
  exporting = false,
  enableDragDrop = true,
}) => {
  const page    = pageSizeMap[pageSize]   || pageSizeMap.A4;
  const pagePx  = pageSizePxMap[pageSize] || pageSizePxMap.A4;

  // ── always derive themeColor from prop so DesignFormatting changes reflect instantly ──
  const themeColor = color || theme;

  const [personalData,          setPersonalData]          = useState({});
  const [photoPreview,          setPhotoPreview]          = useState('');
  const [savedSummary,          setSavedSummary]          = useState('');
  const [savedSkills,           setSavedSkills]           = useState([]);
  const [educationEntries,      setEducationEntries]      = useState([]);
  const [workExperiences,       setWorkExperiences]       = useState([]);
  const [savedCertifications,   setSavedCertifications]   = useState([]);
  const [savedLanguages,        setSavedLanguages]        = useState([]);
  const [savedAccomplishments,  setSavedAccomplishments]  = useState([]);
  const [savedAdditionalInfo,   setSavedAdditionalInfo]   = useState([]);
  const [savedSoftware,         setSavedSoftware]         = useState([]);
  const [savedReferences,       setSavedReferences]       = useState([]);
  const [savedVolunteering,     setSavedVolunteering]     = useState([]);
  const [savedInterests,        setSavedInterests]        = useState([]);
  const [savedWebsites,         setSavedWebsites]         = useState([]);
  const [savedAwards,           setSavedAwards]           = useState([]);
  const [customSections,        setCustomSections]        = useState({});

  const [leftSectionOrder,  setLeftSectionOrder]  = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);

  const [dialogLeft,          setDialogLeft]          = useState([]);
  const [dialogRight,         setDialogRight]         = useState([]);
  const [dialogDragOverIndex, setDialogDragOverIndex] = useState(null);
  const [dialogDragOverSide,  setDialogDragOverSide]  = useState(null);

  const [currentPage,  setCurrentPage]  = useState(0);
  const [totalPages,   setTotalPages]   = useState(1);
  const [pageBreaks,   setPageBreaks]   = useState([]);

  // separate refs for left and right measurement
  const leftMeasureRef  = useRef(null);
  const rightMeasureRef = useRef(null);

  // pagination state for left column
  const [leftPageChunks,  setLeftPageChunks]  = useState([defaultLeftSectionOrder]);
  const [rightPageChunks, setRightPageChunks] = useState([defaultRightSectionOrder]);

  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // ── style helpers (derived from props — always up to date) ────────────────
  const globalFontStyle = {
    fontFamily: font, fontSize, fontStyle,
    lineHeight: `${lineSpacing}px`,
    wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word',
  };
  const getTextStyle = (extra = {}) => ({ ...globalFontStyle, ...extra });
  const overflowSx = {
    wordWrap: 'break-word', overflowWrap: 'break-word',
    wordBreak: 'break-word', maxWidth: '100%', overflow: 'hidden',
  };
  const sectionTitleSx = {
    color: themeColor, fontWeight: 700, mb: 2,
    borderBottom: `2px solid ${themeColor}`, pb: 0.5,
    fontFamily: font, fontSize: headingSize,
    fontStyle, lineHeight: `${lineSpacing}px`,
    ...overflowSx,
  };

  // ── ONE-TIME data load from localStorage ─────────────────────────────────
  // Only re-runs when formData or photo reference changes (not on every design setting change)
  useEffect(() => {
    // personal data: merge localStorage + formData prop
    let personal = {};
    const stored = localStorage.getItem('personalData');
    if (stored) { try { personal = JSON.parse(stored); } catch {} }
    if (formData && Object.keys(formData).length > 0) personal = { ...personal, ...formData };
    setPersonalData(personal);

    // photo
    if (photo) {
      setPhotoPreview(photo);
    } else if (personal.photo) {
      setPhotoPreview(
        personal.photo.startsWith('http')
          ? personal.photo
          : `https://resume-maker-lc.onrender.com${personal.photo}`
      );
    }

    // summary
    try {
      const summaries = JSON.parse(localStorage.getItem('summaries')) || [];
      if (summaries.length > 0) setSavedSummary(summaries[summaries.length - 1]);
    } catch {}

    // education
    try {
      const entries = localStorage.getItem('educationEntries');
      if (entries) setEducationEntries(JSON.parse(entries));
    } catch {}

    // skills
    try {
      const skillsStr = localStorage.getItem('skills');
      if (skillsStr) setSavedSkills(JSON.parse(skillsStr));
    } catch {}

    // work & all list sections
    const safe = (key) => { try { return JSON.parse(localStorage.getItem(key)) || []; } catch { return []; } };
    setWorkExperiences(safe('workExperiences'));
    setSavedWebsites(safe('websites'));
    setSavedCertifications(safe('certificationsList'));
    setSavedLanguages(safe('languagesList'));
    setSavedAccomplishments(safe('accomplishmentsList'));
    setSavedAdditionalInfo(safe('additionalInfo'));
    setSavedSoftware(safe('softwareSkills'));
    setSavedReferences(safe('referencesList'));
    setSavedVolunteering(safe('volunteering'));
    setSavedInterests(safe('interests'));
    setSavedAwards(safe('awardsList'));

    // custom sections
    const userId = localStorage.getItem('userId');
    let sectionsData = {};
    if (userId) {
      const currentCustom = safe(`current_custom_sections_${userId}`);
      currentCustom.forEach(name => {
        try {
          const raw = localStorage.getItem(`custom_${userId}_${name}`);
          if (raw) {
            const parsed = JSON.parse(raw);
            const valid = (parsed.items || []).filter(i => i && i.trim() !== '');
            if (valid.length > 0) sectionsData[name] = valid;
          }
        } catch (e) { console.error(`Error loading custom section ${name}:`, e); }
      });
    }
    setCustomSections(sectionsData);

    // section order
    const customIds = Object.keys(sectionsData).map(n => `custom_${n}`);
    const savedLeftOrder  = localStorage.getItem('resume47LeftSectionOrder');
    const savedRightOrder = localStorage.getItem('resume47RightSectionOrder');

    if (savedLeftOrder) {
      try { setLeftSectionOrder(JSON.parse(savedLeftOrder)); } catch { setLeftSectionOrder(defaultLeftSectionOrder); }
    } else {
      setLeftSectionOrder(defaultLeftSectionOrder);
    }

    if (savedRightOrder) {
      try {
        const parsedRight   = JSON.parse(savedRightOrder);
        const filteredRight = parsedRight.filter(s => !s.startsWith('custom_'));
        setRightSectionOrder([...filteredRight, ...customIds]);
      } catch {
        setRightSectionOrder([...defaultRightSectionOrder, ...customIds]);
      }
    } else {
      setRightSectionOrder([...defaultRightSectionOrder, ...customIds]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, photo]);

  // sync new custom sections into rightSectionOrder without duplicating
  useEffect(() => {
    const userId = localStorage.getItem('userId');
    if (!userId || Object.keys(customSections).length === 0) return;
    const customIds = Object.keys(customSections).map(n => `custom_${n}`);
    setRightSectionOrder(prev => {
      const filtered = prev.filter(s => !s.startsWith('custom_'));
      const next = [...filtered, ...customIds];
      if (JSON.stringify(next) !== JSON.stringify(prev)) {
        localStorage.setItem('resume47RightSectionOrder', JSON.stringify(next));
        return next;
      }
      return prev;
    });
  }, [customSections]);

  // persist section order changes
  useEffect(() => {
    if (leftSectionOrder.length  > 0) localStorage.setItem('resume47LeftSectionOrder',  JSON.stringify(leftSectionOrder));
    if (rightSectionOrder.length > 0) localStorage.setItem('resume47RightSectionOrder', JSON.stringify(rightSectionOrder));
  }, [leftSectionOrder, rightSectionOrder]);

  const formatDate = (month, year) => (!month || !year) ? '' : `${month} ${year}`;

  const getSectionTitle = (id) => {
    const t = {
      summary: 'SUMMARY', skills: 'SKILLS', experience: 'EXPERIENCE', education: 'EDUCATION',
      awards: 'AWARD', references: 'REFERENCE', certifications: 'CERTIFICATIONS', languages: 'LANGUAGES',
      accomplishments: 'ACCOMPLISHMENTS', additionalInfo: 'ADDITIONAL INFORMATION',
      softwareSkills: 'SOFTWARE SKILLS', volunteering: 'VOLUNTEERING', interests: 'INTERESTS',
      websites: 'WEBSITES & PROFILES',
    };
    if (id.startsWith('custom_')) return id.replace('custom_', '').replace(/_/g, ' ').toUpperCase();
    return t[id] || id.toUpperCase();
  };

  // ── section content renderer ──────────────────────────────────────────────
  const renderSectionContent = useCallback((sectionId, colClass) => {
    // colClass: 'r47-left-section' or 'r47-right-section' for separate measurement
    const cls = colClass || 'r47-right-section';

    switch (sectionId) {
      case 'summary':
        return savedSummary ? (
          <Box key="summary" className={cls} sx={{ mb: `${sectionSpacing}px`, ...overflowSx }}>
            <Typography variant="h6" sx={sectionTitleSx}>SUMMARY</Typography>
            <Typography variant="body1" sx={getTextStyle()}>{savedSummary}</Typography>
          </Box>
        ) : null;

      case 'skills':
        return savedSkills.length > 0 ? (
          <Box key="skills" className={cls} sx={{ mb: `${sectionSpacing}px`, ...overflowSx }}>
            <Typography variant="h6" sx={sectionTitleSx}>SKILLS</Typography>
            {savedSkills.map((skill, i) => (
              <Typography key={i} variant="body2" sx={getTextStyle({ mb: `${paragraphSpacing}px` })}>• {skill.name}</Typography>
            ))}
          </Box>
        ) : null;

      case 'experience':
        return workExperiences.length > 0 ? (
          <Box key="experience" className={cls} sx={{ mb: `${sectionSpacing}px`, ...overflowSx }}>
            <Typography variant="h6" sx={sectionTitleSx}>EXPERIENCE</Typography>
            {workExperiences.map((work, i) => (
              <Box key={i} sx={{ mb: `${paragraphSpacing * 1.5}px`, ...overflowSx }}>
                <Typography variant="subtitle1" sx={getTextStyle({ fontWeight: 700, color: themeColor })}>{work.jobTitle}</Typography>
                <Typography variant="body2" color="text.secondary" sx={getTextStyle({ mb: `${paragraphSpacing / 2}px` })}>
                  {work.employer} • {formatDate(work.startMonth, work.startYear)} – {work.current ? 'Present' : formatDate(work.endMonth, work.endYear)}
                </Typography>
                {work.description && work.description.split('\n').filter(l => l.trim()).map((line, idx) => (
                  <Typography key={idx} variant="body2" sx={getTextStyle({ mb: `${paragraphSpacing}px` })}>{line}</Typography>
                ))}
              </Box>
            ))}
          </Box>
        ) : null;

      case 'education':
        return educationEntries.length > 0 ? (
          <Box key="education" className={cls} sx={{ mb: `${sectionSpacing}px`, ...overflowSx }}>
            <Typography variant="h6" sx={sectionTitleSx}>EDUCATION</Typography>
            {educationEntries.map((edu, i) => {
              const institution = edu.educationLevel === 'school' ? edu.schoolName  : edu.collegeName;
              const degree      = edu.educationLevel === 'school' ? edu.standard    : edu.degreeName;
              const field       = edu.educationLevel === 'school' ? edu.stream      : edu.specialization;
              return (
                <Box key={i} sx={{ mb: `${paragraphSpacing}px`, ...overflowSx }}>
                  <Typography variant="subtitle1" sx={getTextStyle({ fontWeight: 700 })}>{formatDate(edu.gradMonth, edu.gradYear)}</Typography>
                  <Typography variant="body1"     sx={getTextStyle({ fontWeight: 600 })}>{institution}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={getTextStyle()}>{degree}{field && `, ${field}`}</Typography>
                </Box>
              );
            })}
          </Box>
        ) : null;

      case 'awards':
        return savedAwards.length > 0 ? (
          <Box key="awards" className={cls} sx={{ mb: `${sectionSpacing}px`, ...overflowSx }}>
            <Typography variant="h6" sx={sectionTitleSx}>AWARD</Typography>
            {savedAwards.map((award, i) => (
              <Typography key={i} variant="body2" sx={getTextStyle({ mb: `${paragraphSpacing}px` })}>• {award}</Typography>
            ))}
          </Box>
        ) : null;

      case 'references':
        return savedReferences.length > 0 ? (
          <Box key="references" className={cls} sx={{ mb: `${sectionSpacing}px`, ...overflowSx }}>
            <Typography variant="h6" sx={sectionTitleSx}>REFERENCE</Typography>
            {savedReferences.map((ref, i) => {
              let refData;
              try { refData = typeof ref === 'string' ? JSON.parse(ref) : ref; }
              catch  { refData = { name: String(ref) }; }
              return (
                <Box key={i} sx={{ mb: `${paragraphSpacing}px`, ...overflowSx }}>
                  {refData.name     && <Typography variant="body2" sx={getTextStyle({ fontWeight: 'bold' })}>{refData.name}</Typography>}
                  {(refData.position || refData.company) && <Typography variant="body2" sx={getTextStyle()}>{refData.position}{refData.company ? `, ${refData.company}` : ''}</Typography>}
                  {refData.phone    && <Typography variant="body2" sx={getTextStyle()}>📞 {refData.phone}</Typography>}
                  {refData.email    && <Typography variant="body2" sx={getTextStyle()}>✉️ {refData.email}</Typography>}
                </Box>
              );
            })}
          </Box>
        ) : null;

      case 'certifications':
        return savedCertifications.length > 0 ? (
          <Box key="certifications" className={cls} sx={{ mb: `${sectionSpacing}px`, ...overflowSx }}>
            <Typography variant="h6" sx={sectionTitleSx}>CERTIFICATIONS</Typography>
            {savedCertifications.map((cert, i) => (
              <Typography key={i} variant="body2" sx={getTextStyle({ mb: `${paragraphSpacing}px` })}>
                • {cert.name} – {cert.provider} ({cert.year})
              </Typography>
            ))}
          </Box>
        ) : null;

      case 'languages':
        return savedLanguages.length > 0 ? (
          <Box key="languages" className={cls} sx={{ mb: `${sectionSpacing}px`, ...overflowSx }}>
            <Typography variant="h6" sx={sectionTitleSx}>LANGUAGES</Typography>
            {savedLanguages.map((lang, i) => (
              <Typography key={i} variant="body2" sx={getTextStyle({ mb: `${paragraphSpacing}px` })}>
                • {lang.name} {lang.level ? `– ${lang.level}` : ''}
              </Typography>
            ))}
          </Box>
        ) : null;

      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <Box key="accomplishments" className={cls} sx={{ mb: `${sectionSpacing}px`, ...overflowSx }}>
            <Typography variant="h6" sx={sectionTitleSx}>ACCOMPLISHMENTS</Typography>
            {savedAccomplishments.map((acc, i) => (
              <Typography key={i} variant="body2" sx={getTextStyle({ mb: `${paragraphSpacing}px` })}>• {acc}</Typography>
            ))}
          </Box>
        ) : null;

      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <Box key="additionalInfo" className={cls} sx={{ mb: `${sectionSpacing}px`, ...overflowSx }}>
            <Typography variant="h6" sx={sectionTitleSx}>ADDITIONAL INFORMATION</Typography>
            {savedAdditionalInfo.map((info, i) => (
              <Typography key={i} variant="body2" sx={getTextStyle({ mb: `${paragraphSpacing}px` })}>• {info}</Typography>
            ))}
          </Box>
        ) : null;

      case 'softwareSkills':
        return savedSoftware.length > 0 ? (
          <Box key="softwareSkills" className={cls} sx={{ mb: `${sectionSpacing}px`, ...overflowSx }}>
            <Typography variant="h6" sx={sectionTitleSx}>SOFTWARE SKILLS</Typography>
            {savedSoftware.map((sw, i) => (
              <Typography key={i} variant="body2" sx={getTextStyle({ mb: `${paragraphSpacing}px` })}>• {sw.name} – {sw.level}%</Typography>
            ))}
          </Box>
        ) : null;

      case 'volunteering':
        return savedVolunteering.length > 0 ? (
          <Box key="volunteering" className={cls} sx={{ mb: `${sectionSpacing}px`, ...overflowSx }}>
            <Typography variant="h6" sx={sectionTitleSx}>VOLUNTEERING</Typography>
            {savedVolunteering.map((vol, i) => (
              <Box key={i} sx={{ mb: `${paragraphSpacing}px`, ...overflowSx }}>
                <Typography variant="body2" sx={getTextStyle({ fontWeight: 'bold' })}>{vol.subtopic}</Typography>
                <Typography variant="body2" sx={getTextStyle({ fontStyle: 'italic' })}>{vol.fromDate} - {vol.toDate}</Typography>
                <Typography variant="body2" sx={getTextStyle()}>{vol.content}</Typography>
              </Box>
            ))}
          </Box>
        ) : null;

      case 'interests':
        return savedInterests.length > 0 ? (
          <Box key="interests" className={cls} sx={{ mb: `${sectionSpacing}px`, ...overflowSx }}>
            <Typography variant="h6" sx={sectionTitleSx}>INTERESTS</Typography>
            {savedInterests.map((interest, i) => (
              <Typography key={i} variant="body2" sx={getTextStyle({ mb: `${paragraphSpacing}px` })}>• {interest}</Typography>
            ))}
          </Box>
        ) : null;

      case 'websites':
        return savedWebsites.length > 0 ? (
          <Box key="websites" className={cls} sx={{ mb: `${sectionSpacing}px`, ...overflowSx }}>
            <Typography variant="h6" sx={sectionTitleSx}>WEBSITES & PROFILES</Typography>
            {savedWebsites.map((site, i) => (
              <Typography key={i} variant="body2" sx={getTextStyle({ wordBreak: 'break-all', mb: `${paragraphSpacing}px` })}>• {site.url || site}</Typography>
            ))}
          </Box>
        ) : null;

      default:
        if (sectionId && sectionId.startsWith('custom_')) {
          const customName = sectionId.replace('custom_', '');
          const items = (customSections[customName] || []).filter(i => i && i.trim() !== '');
          if (items.length > 0) {
            return (
              <Box key={sectionId} className={cls} sx={{ mb: `${sectionSpacing}px`, ...overflowSx }}>
                <Typography variant="h6" sx={sectionTitleSx}>{customName.replace(/_/g, ' ').toUpperCase()}</Typography>
                {items.map((item, i) => (
                  <Typography key={i} variant="body2" sx={getTextStyle({ mb: `${paragraphSpacing}px` })}>• {item}</Typography>
                ))}
              </Box>
            );
          }
        }
        return null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    savedSummary, savedSkills, workExperiences, educationEntries,
    savedAwards, savedReferences, savedCertifications, savedLanguages,
    savedAccomplishments, savedAdditionalInfo, savedSoftware,
    savedVolunteering, savedInterests, savedWebsites, customSections,
    themeColor, font, fontSize, fontStyle, headingSize,
    sectionSpacing, paragraphSpacing, lineSpacing,
  ]);

  // ── PAGINATION: compute left + right page chunks from measured heights ────
  useEffect(() => {
    if (exporting) return;

    const timer = setTimeout(() => {
      const pageH      = pagePx.height - topBottomMargin * 2;
      const headerH    = 310; // header height on page 0

      // ── RIGHT column pagination ──
      const rightContainer = rightMeasureRef.current;
      if (rightContainer) {
        const rightSections   = Array.from(rightContainer.querySelectorAll('.r47-right-section'));
        const visibleRightIds = rightSectionOrder.filter(id => renderSectionContent(id, 'r47-right-section') !== null);

        const rChunks  = [[]];
        let rAccum     = 0;
        let rPageIdx   = 0;

        rightSections.forEach((el, i) => {
          const secId    = visibleRightIds[i];
          if (!secId) return;
          const h        = el.getBoundingClientRect().height + (sectionSpacing || 0);
          const limit    = pageH - (rPageIdx === 0 ? headerH : 0);
          if (rAccum + h > limit && rChunks[rPageIdx].length > 0) {
            rPageIdx++;
            rChunks.push([]);
            rAccum = 0;
          }
          rChunks[rPageIdx].push(secId);
          rAccum += h;
        });

        setRightPageChunks(rChunks);

        // ── LEFT column pagination: match page count of right ──
        const leftContainer  = leftMeasureRef.current;
        const totalPgs       = Math.max(1, rChunks.length);
        setTotalPages(totalPgs);

        if (leftContainer) {
          const leftSections   = Array.from(leftContainer.querySelectorAll('.r47-left-section'));
          const visibleLeftIds = leftSectionOrder.filter(id => renderSectionContent(id, 'r47-left-section') !== null);

          const lChunks  = [[]];
          let lAccum     = 0;
          let lPageIdx   = 0;

          leftSections.forEach((el, i) => {
            const secId  = visibleLeftIds[i];
            if (!secId) return;
            const h      = el.getBoundingClientRect().height + (sectionSpacing || 0);
            const limit  = pageH - (lPageIdx === 0 ? headerH : 0);
            if (lAccum + h > limit && lChunks[lPageIdx].length > 0) {
              lPageIdx++;
              lChunks.push([]);
              lAccum = 0;
            }
            lChunks[lPageIdx].push(secId);
            lAccum += h;
          });

          // pad lChunks to match totalPgs
          while (lChunks.length < totalPgs) lChunks.push([]);
          setLeftPageChunks(lChunks);
        }

        setPageBreaks(rChunks.map((_, idx) => idx));
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [
    rightSectionOrder, leftSectionOrder,
    savedSummary, workExperiences, educationEntries,
    savedSkills, savedLanguages, savedAccomplishments, savedCertifications,
    savedReferences, savedSoftware, savedVolunteering, savedInterests,
    savedWebsites, savedAdditionalInfo, customSections, savedAwards,
    topBottomMargin, pagePx.height, sectionSpacing, paragraphSpacing,
    lineSpacing, fontSize, headingSize, font, exporting, renderSectionContent,
  ]);

  // clamp currentPage
  useEffect(() => {
    if (currentPage > totalPages - 1) setCurrentPage(Math.max(0, totalPages - 1));
  }, [totalPages, currentPage]);

  // ── Arrange dialog ────────────────────────────────────────────────────────
  useEffect(() => {
    if (arrangeDialogOpen) {
      setDialogLeft([...leftSectionOrder]);
      setDialogRight([...rightSectionOrder]);
      setDialogDragOverIndex(null);
      setDialogDragOverSide(null);
    }
  }, [arrangeDialogOpen, leftSectionOrder, rightSectionOrder]);

  const handleDialogDragStart = (e, side, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ side, index }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDialogDragOver = (e, index, side) => {
    e.preventDefault();
    e.stopPropagation();
    setDialogDragOverIndex(index);
    setDialogDragOverSide(side);
  };

  const handleDialogDragLeave = () => {
    setDialogDragOverIndex(null);
    setDialogDragOverSide(null);
  };

  const handleDialogDrop = (e, targetIndex, targetSide) => {
    e.preventDefault();
    e.stopPropagation();

    let data;
    try { data = JSON.parse(e.dataTransfer.getData('text/plain')); }
    catch { setDialogDragOverIndex(null); setDialogDragOverSide(null); return; }

    const { side: sourceSide, index: sourceIndex } = data;
    const left  = [...dialogLeft];
    const right = [...dialogRight];

    let movedItem;
    if (sourceSide === 'left') { movedItem = left[sourceIndex];  left.splice(sourceIndex, 1); }
    else                       { movedItem = right[sourceIndex]; right.splice(sourceIndex, 1); }

    if (!movedItem) { setDialogDragOverIndex(null); setDialogDragOverSide(null); return; }

    let insertIndex = targetIndex;
    if (sourceSide === targetSide && sourceIndex < targetIndex) insertIndex = targetIndex - 1;

    if (targetSide === 'left')  left.splice(Math.max(0, insertIndex), 0, movedItem);
    else                        right.splice(Math.max(0, insertIndex), 0, movedItem);

    setDialogLeft(left);
    setDialogRight(right);
    setLeftSectionOrder(left);
    setRightSectionOrder(right);
    setDialogDragOverIndex(null);
    setDialogDragOverSide(null);
  };

  const handleDialogDragEnd = (e) => {
    setDialogDragOverIndex(null);
    setDialogDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const resetSectionOrder = () => {
    const customIds = Object.keys(customSections).map(n => `custom_${n}`);
    setLeftSectionOrder(defaultLeftSectionOrder);
    setRightSectionOrder([...defaultRightSectionOrder, ...customIds]);
    localStorage.removeItem('resume47LeftSectionOrder');
    localStorage.removeItem('resume47RightSectionOrder');
  };

  // ── Arrange dialog panel ──────────────────────────────────────────────────
  const renderTwoColumnDragPanel = () => (
    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
      {[
        { label: 'Left Column Sections',  list: dialogLeft,  side: 'left'  },
        { label: 'Right Column Sections', list: dialogRight, side: 'right' },
      ].map(({ label, list, side }) => (
        <Box key={side} sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <DragIndicator sx={{ mr: 1 }} />{label}
            <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>({list.length})</Typography>
          </Typography>
          <Box
            sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}
            onDragOver={(e) => { e.preventDefault(); setDialogDragOverIndex(list.length); setDialogDragOverSide(side); }}
            onDrop={(e) => handleDialogDrop(e, list.length, side)}
          >
            {list.length > 0 ? list.map((sectionId, index) => (
              <Paper
                key={sectionId}
                draggable
                onDragStart={(e) => handleDialogDragStart(e, side, index)}
                onDragOver={(e)  => handleDialogDragOver(e, index, side)}
                onDragLeave={handleDialogDragLeave}
                onDrop={(e)      => handleDialogDrop(e, index, side)}
                onDragEnd={handleDialogDragEnd}
                sx={{
                  p: 2, mb: 1, display: 'flex', alignItems: 'center',
                  backgroundColor: dialogDragOverIndex === index && dialogDragOverSide === side ? '#e3f2fd' : 'white',
                  borderTop: dialogDragOverIndex === index && dialogDragOverSide === side ? '2px solid #1976d2' : '2px solid transparent',
                  boxShadow: dialogDragOverIndex === index && dialogDragOverSide === side ? 2 : 1,
                  cursor: 'grab', '&:active': { cursor: 'grabbing' }, transition: 'all 0.2s ease',
                }}
              >
                <DragIndicator color="action" />
                <Typography sx={{ ml: 1, flexGrow: 1 }}>{getSectionTitle(sectionId)}</Typography>
                <Typography variant="body2" color="textSecondary">{index + 1}</Typography>
              </Paper>
            )) : (
              <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
                No sections. Drag from the {side === 'left' ? 'right' : 'left'}.
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );

  // ── header ────────────────────────────────────────────────────────────────
  const renderHeader = () => (
    <>
      <Box sx={{ height: '140px', bgcolor: '#e3f2fd', position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
        <Avatar
          src={photoPreview || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&auto=format&fit=crop&q=80'}
          sx={{
            width: 140, height: 140,
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            border: '8px solid white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        />
      </Box>
      <Box sx={{ textAlign: 'center', pt: 5, pb: 2, px: `${sideMargins}px`, overflow: 'hidden' }}>
        <Typography component="h1" sx={{
          fontWeight: 700, letterSpacing: '1.5px', color: themeColor,
          mb: 1, fontFamily: font, fontStyle,
          fontSize: `calc(${headingSize} * 1.6)`,
          lineHeight: 1.2, wordBreak: 'break-word',
        }}>
          {(personalData.firstName?.toUpperCase()  || 'DANI')} {(personalData.lastName?.toUpperCase() || 'MARTINEZ')}
        </Typography>
        <Typography sx={{
          color: themeColor, fontWeight: 600, letterSpacing: '1px',
          fontFamily: font, fontStyle,
          fontSize: headingSize, lineHeight: `${lineSpacing}px`, wordBreak: 'break-word',
        }}>
          {(personalData.currentPosition?.toUpperCase() || 'WEB DEVELOPER')}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, px: `${sideMargins}px`, pb: 3, overflow: 'hidden' }}>
        {personalData.phone && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PhoneIcon sx={{ color: themeColor, fontSize: 18, flexShrink: 0 }} />
            <Typography sx={getTextStyle({ wordBreak: 'break-word' })}>{personalData.phone}</Typography>
          </Box>
        )}
        {personalData.email && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <EmailIcon sx={{ color: themeColor, fontSize: 18, flexShrink: 0 }} />
            <Typography sx={getTextStyle({ wordBreak: 'break-all' })}>{personalData.email}</Typography>
          </Box>
        )}
        {(personalData.city || personalData.country) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocationOnIcon sx={{ color: themeColor, fontSize: 18, flexShrink: 0 }} />
            <Typography sx={getTextStyle()}>{[personalData.city, personalData.country].filter(Boolean).join(', ')}</Typography>
          </Box>
        )}
        {personalData.linkedIn && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LinkedInIcon sx={{ color: '#0077b5', fontSize: 18, flexShrink: 0 }} />
            <a href={personalData.linkedIn.startsWith('http') ? personalData.linkedIn : `https://${personalData.linkedIn}`}
              target="_blank" rel="noopener noreferrer"
              style={{ color: '#0077b5', textDecoration: 'none', fontFamily: font, fontSize, wordBreak: 'break-all' }}>
              {personalData.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, '')}
            </a>
          </Box>
        )}
        {personalData.website && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LanguageIcon sx={{ color: '#1976d2', fontSize: 18, flexShrink: 0 }} />
            <a href={personalData.website.startsWith('http') ? personalData.website : `https://${personalData.website}`}
              target="_blank" rel="noopener noreferrer"
              style={{ color: '#1976d2', textDecoration: 'none', fontFamily: font, fontSize, wordBreak: 'break-all' }}>
              {personalData.website.replace(/^https?:\/\/(www\.)?/, '')}
            </a>
          </Box>
        )}
        {personalData.drivingLicence && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DriveEtaIcon sx={{ color: '#4caf50', fontSize: 18, flexShrink: 0 }} />
            <Typography sx={getTextStyle()}>DL: {personalData.drivingLicence}</Typography>
          </Box>
        )}
      </Box>
    </>
  );

  // ── render one page (editor or export) ───────────────────────────────────
  const renderPage = (pageIdx, elevation, paperSx) => {
    const lSections = (leftPageChunks[pageIdx]  || []);
    const rSections = (rightPageChunks[pageIdx] || []);
    return (
      <Paper key={pageIdx} elevation={elevation} className="resume-page" sx={paperSx}>
        {pageIdx === 0 && renderHeader()}
        <Box sx={{
          display: 'flex', gap: `${sideMargins}px`, px: `${sideMargins}px`,
          boxSizing: 'border-box', alignItems: 'flex-start',
          height: pageIdx === 0 ? 'calc(100% - 310px)' : '100%',
          overflow: 'hidden',
        }}>
          {/* Left column — paginated */}
          <Box sx={{ width: '35%', minWidth: '35%', maxWidth: '35%', flexShrink: 0, boxSizing: 'border-box' }}>
            {lSections.map(id => (
              <React.Fragment key={id}>{renderSectionContent(id, 'r47-left-section')}</React.Fragment>
            ))}
          </Box>
          {/* Right column — paginated */}
          <Box sx={{ flex: 1, minWidth: 0, boxSizing: 'border-box' }}>
            {rSections.map(id => (
              <React.Fragment key={id}>{renderSectionContent(id, 'r47-right-section')}</React.Fragment>
            ))}
          </Box>
        </Box>
      </Paper>
    );
  };

  const editorPaperSx = {
    width: page.width, minHeight: page.height, maxHeight: page.height,
    mx: 'auto', bgcolor: 'white', overflow: 'hidden',
    boxSizing: 'border-box', pt: `${topBottomMargin}px`, pb: `${topBottomMargin}px`,
  };

  const exportPaperSx = (pageIdx) => ({
    width: page.width, height: page.height,
    mx: 'auto', mb: '24px', bgcolor: 'white',
    overflow: 'hidden', boxSizing: 'border-box',
    pt: `${topBottomMargin}px`, pb: `${topBottomMargin}px`,
    pageBreakAfter: 'always',
    '@media print': {
      width: page.width, height: page.height, p: 0,
      pt: `${topBottomMargin}px`, pb: `${topBottomMargin}px`,
      margin: 0, boxShadow: 'none', pageBreakAfter: 'always',
    },
  });

  // ── JSX ───────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ bgcolor: '#f8f9fa', display: 'flex', flexDirection: 'column', alignItems: 'center', p: exporting ? 0 : 3 }}>

      {/* Controls */}
      {!exporting && (
        <Box sx={{ mb: 2, '@media print': { display: 'none' }, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
          <IconButton onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0} size="small">
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">Page {currentPage + 1} / {totalPages}</Typography>
          <IconButton onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))} disabled={currentPage >= totalPages - 1} size="small">
            <ChevronRight />
          </IconButton>
          {enableDragDrop && (
            <>
              <Button onClick={() => setArrangeDialogOpen(true)} variant="contained" startIcon={<Reorder />} color="primary" size="small">
                Arrange Sections
              </Button>
              <Button onClick={resetSectionOrder} variant="outlined" color="secondary" size="small">
                Reset Sections
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Hidden LEFT measure container */}
      <Box
        ref={leftMeasureRef}
        sx={{
          position: 'absolute', visibility: 'hidden', pointerEvents: 'none',
          width: `${pagePx.width * 0.35 - sideMargins * 1.5}px`,
          top: 0, left: -9999, zIndex: -1,
        }}
      >
        {leftSectionOrder.map(id => {
          const c = renderSectionContent(id, 'r47-left-section');
          return c ? <React.Fragment key={id}>{c}</React.Fragment> : null;
        })}
      </Box>

      {/* Hidden RIGHT measure container */}
      <Box
        ref={rightMeasureRef}
        sx={{
          position: 'absolute', visibility: 'hidden', pointerEvents: 'none',
          width: `${pagePx.width * 0.65 - sideMargins * 1.5}px`,
          top: 0, left: -9999, zIndex: -1,
        }}
      >
        {rightSectionOrder.map(id => {
          const c = renderSectionContent(id, 'r47-right-section');
          return c ? <React.Fragment key={id}>{c}</React.Fragment> : null;
        })}
      </Box>

      {/* Pages */}
      <Box id="resume-container">
        {exporting
          ? Array.from({ length: totalPages }).map((_, idx) => renderPage(idx, 0, exportPaperSx(idx)))
          : renderPage(currentPage, 3, editorPaperSx)
        }
      </Box>

      {/* Arrange dialog */}
      <Dialog open={arrangeDialogOpen} onClose={() => setArrangeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center"><Reorder sx={{ mr: 1 }} />Arrange Resume Sections</Box>
        </DialogTitle>
        <DialogContent>{renderTwoColumnDragPanel()}</DialogContent>
        <DialogActions>
          <Button onClick={resetSectionOrder} color="secondary">Reset</Button>
          <Button onClick={() => setArrangeDialogOpen(false)} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume47;